
// ... (keep imports)
import { initializeApp, getApps, getApp, FirebaseApp } from "firebase/app";
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import {
  getFirestore,
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  setDoc,
  getDoc,
  enableIndexedDbPersistence,
  limit,
  Timestamp,
  orderBy,
  onSnapshot,
  Firestore,
  or,
  writeBatch,
  Unsubscribe
} from "firebase/firestore";
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithCredential,
  Auth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
  updateProfile,
  User as FirebaseUser
} from "firebase/auth";
import { Capacitor } from '@capacitor/core';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import {
  Task, User, UserRole, UserTier, EmailLog, Category, Quadrant,
  MessageTemplate, TriggerAssociation, SystemEvent, Broadcast,
  UserNotification, TrustDocument, TrustVersion,
  TrustAuditLog, Actor, SocialLink, SystemConfig, PostmarkConfig,
  Feedback, FeedbackStatus
} from '../types';
import { CORE_TEMPLATES, CORE_ASSOCIATIONS } from '../constants';

const firebaseConfig = {
  apiKey: "AIzaSyDvt5XBJ0wsqz01qx0_wvoYjWmu9zvbsyY",
  authDomain: "priori-ai-1.firebaseapp.com",
  projectId: "priori-ai-1",
  storageBucket: "priori-ai-1.firebasestorage.app",
  messagingSenderId: "1054213064192",
  appId: "1:1054213064192:web:df25bda4fdbaa5682c3ae5",
  measurementId: "G-VS5SKCDBFN"
};

let app: FirebaseApp;
let firestore: Firestore;
let auth: Auth;
let storage: ReturnType<typeof getStorage>;

try {
  if (getApps().length === 0) {
    app = initializeApp(firebaseConfig);
    console.log("[Vanguard-Core] Firebase App Initialized via Official gstatic CDN");
  } else {
    app = getApp();
  }

  firestore = getFirestore(app);
  auth = getAuth(app);
  storage = getStorage(app);

  if (firestore) {
    try {
      enableIndexedDbPersistence(firestore).catch((err) => {
        if (err.code === 'failed-precondition') console.warn("Persistence failed: Multiple tabs open.");
        else if (err.code === 'unimplemented') console.warn("Persistence failed: Browser not supported.");
      });
    } catch (e) { }
  }
} catch (error) {
  console.error("[Vanguard-Core] Firebase Initialization Failed:", error);
}

export { firestore, auth };

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

export function cleanForFirestore(obj: any, seen = new WeakSet()): any {
  if (obj === null || typeof obj !== 'object') return obj;

  if (obj instanceof Date) return obj.getTime();
  if (obj && typeof obj.toDate === 'function') {
    try { return obj.toDate().getTime(); } catch { return Date.now(); }
  }

  // Handle Firebase specific circular structures and minified internals (UserImpl, Y, Ka)
  const constructorName = obj.constructor?.name;
  const isInternal = (
    constructorName === 'Y' ||
    constructorName === 'Ka' ||
    constructorName === 'Firestore' ||
    constructorName === 'Auth' ||
    constructorName === 'FirebaseAppImpl' ||
    (constructorName && constructorName.length <= 2) || // Heuristic for minified internal classes
    obj._delegate ||
    obj.firestore ||
    obj.database ||
    obj.app ||
    obj.stsTokenManager || // Auth internal
    (obj.i && obj.i.src === obj) || // Specific circular reference pattern reported
    (obj.proactiveRefresh && obj.reloadUserInfo) // UserImpl detection
  );

  if (isInternal) return undefined;

  if (seen.has(obj)) return undefined;
  seen.add(obj);

  if (Array.isArray(obj)) {
    return obj.map(item => cleanForFirestore(item, seen)).filter(i => i !== undefined);
  }

  // Treat as plain object
  const cleaned: any = {};

  // Safe iteration over keys
  try {
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      // Filter out internal properties starting with _ or functions
      if (typeof value === 'function' || key.startsWith('_')) return;

      const cleanedValue = cleanForFirestore(value, seen);
      if (cleanedValue !== undefined) {
        cleaned[key] = cleanedValue;
      }
    });
  } catch (e) {
    // If we can't iterate keys (e.g. proxy revocated or access denied), skip it
    return undefined;
  }

  return cleaned;
}

export const getCircularReplacer = () => {
  const seen = new WeakSet();
  return (key: string, value: any) => {
    if (typeof value === "object" && value !== null) {
      if (seen.has(value)) {
        return;
      }
      seen.add(value);

      const constructorName = value.constructor?.name;
      const isInternal = (
        constructorName === 'Y' ||
        constructorName === 'Ka' ||
        (constructorName && constructorName.length <= 2) ||
        value._delegate ||
        value.stsTokenManager ||
        (value.i && value.i.src === value)
      );

      if (isInternal) {
        return;
      }
    }
    return value;
  };
};

export const getCircularReplacerForJSON = getCircularReplacer; // Alias if needed

class LocalStore {
  private getStore(coll: string): any[] {
    try {
      const data = localStorage.getItem(`priori_vanguard_v3_${coll}`);
      return data ? JSON.parse(data) : [];
    } catch { return []; }
  }

  private setStore(coll: string, data: any[]) {
    try {
      const sanitizedData = cleanForFirestore(data);
      localStorage.setItem(`priori_vanguard_v3_${coll}`, JSON.stringify(sanitizedData, getCircularReplacer()));
    } catch (e) {
      console.error(`[LocalStore] Failed to persist ${coll}`, e);
    }
  }

  async getAll<T>(coll: string): Promise<T[]> { return this.getStore(coll); }

  async add<T extends { id?: string; versionId?: string }>(coll: string, data: T): Promise<T> {
    const store = this.getStore(coll);
    const id = data.id || data.versionId;
    const filtered = store.filter(i => (i.id || i.versionId) !== id);
    filtered.push(data);
    this.setStore(coll, filtered);
    return data;
  }

  async update(coll: string, id: string, updates: any) {
    const store = this.getStore(coll);
    const idx = store.findIndex(i => (i.id || i.versionId) === id);
    if (idx !== -1) {
      store[idx] = { ...store[idx], ...updates };
      this.setStore(coll, store);
    }
  }

  async delete(coll: string, id: string) {
    const store = this.getStore(coll);
    this.setStore(coll, store.filter(i => (i.id || i.versionId) !== id));
  }

  clear() {
    const collections = ['tasks', 'users', 'categories', 'email_logs', 'broadcasts', 'associations', 'templates', 'trust_documents', 'trust_versions', 'trust_audit_log', 'social_links', 'system_config', 'postmark_config'];
    collections.forEach(coll => {
      localStorage.removeItem(`priori_vanguard_v3_${coll}`);
    });
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('priori_vanguard_v3_')) {
        localStorage.removeItem(key);
      }
    }
  }
}

const localStore = new LocalStore();
export const db = localStore;

const DEFAULT_CATEGORY: Category = {
  id: 'primary_sector',
  userId: 'system',
  name: 'Primary Sector',
  color: '#6366f1'
};

// Get current user's Firebase Auth ID token for authenticated API calls
export async function getAuthToken(): Promise<string | null> {
  const user = auth?.currentUser;
  if (!user) return null;
  return user.getIdToken();
}

export async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = await getAuthToken();
  return token ? { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` } : { 'Content-Type': 'application/json' };
}

export const firebaseService = {
  async isBackendAvailable(): Promise<boolean> {
    return !!firestore;
  },

  clearLocalData() {
    localStore.clear();
  },

  async signInWithGoogle(): Promise<{ user: User; isNewUser: boolean }> {
    const result = await signInWithPopup(auth, googleProvider);
    // Use user.providerData or just extract specific fields to avoid passing the full UserImpl object
    const fbUser = result.user;

    // Check if user exists in Firestore
    const userDocRef = doc(firestore, 'users', fbUser.uid);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      return { user: userDoc.data() as User, isNewUser: false };
    } else {
      const newUser: User = {
        id: fbUser.uid,
        email: fbUser.email || '',
        displayName: fbUser.displayName || 'Operator',
        role: UserRole.USER,
        tier: UserTier.FREE,
        createdAt: Date.now(),
        lastLogin: Date.now(),
        subscriptionStatus: 'NONE',
        settings: {
          theme: 'light',
          calendarSynced: false,
          notifications: {
            pushEnabled: true,
            quietHoursEnabled: true,
            quietHoursStart: "22:00",
            quietHoursEnd: "08:00",
            notifyOn: ['nudge_urgent', 'nudge_strategic', 'completed', 'partner_request', 'partner_update', 'daily', 'inactivity_7_days', 'streak_7_day']
          }
        }
      };

      const cleanedUser = cleanForFirestore(newUser);
      if (cleanedUser) {
        await setDoc(userDocRef, cleanedUser);
      }
      return { user: newUser, isNewUser: true };
    }
  },

  async signInWithApple(): Promise<{ user: User; isNewUser: boolean }> {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');

    let fbUser: FirebaseUser;

    if (Capacitor.isNativePlatform()) {
      // Native iOS: use Capacitor plugin, then exchange token with Firebase
      const nonce = Array.from(crypto.getRandomValues(new Uint8Array(32)))
        .map(b => b.toString(16).padStart(2, '0')).join('');
      const hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(nonce));
      const hashedNonce = Array.from(new Uint8Array(hashBuffer))
        .map(b => b.toString(16).padStart(2, '0')).join('');

      const result = await SignInWithApple.authorize({
        clientId: 'com.prioriai.vanguard',
        redirectURI: '',
        scopes: 'email name',
        nonce: hashedNonce,
      });

      const credential = provider.credential({
        idToken: result.response.identityToken,
        rawNonce: nonce,
      });
      const firebaseResult = await signInWithCredential(auth, credential);
      fbUser = firebaseResult.user;
    } else {
      // Web: Firebase popup handles everything
      const result = await signInWithPopup(auth, provider);
      fbUser = result.user;
    }

    const userDocRef = doc(firestore, 'users', fbUser.uid);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      return { user: userDoc.data() as User, isNewUser: false };
    } else {
      const newUser: User = {
        id: fbUser.uid,
        email: fbUser.email || '',
        displayName: fbUser.displayName || 'Operator',
        role: UserRole.USER,
        tier: UserTier.FREE,
        createdAt: Date.now(),
        lastLogin: Date.now(),
        subscriptionStatus: 'NONE',
        settings: {
          theme: 'light',
          calendarSynced: false,
          notifications: {
            pushEnabled: true,
            quietHoursEnabled: true,
            quietHoursStart: "22:00",
            quietHoursEnd: "08:00",
            notifyOn: ['nudge_urgent', 'nudge_strategic', 'completed', 'partner_request', 'partner_update', 'daily', 'inactivity_7_days', 'streak_7_day']
          }
        }
      };
      const cleanedUser = cleanForFirestore(newUser);
      if (cleanedUser) await setDoc(userDocRef, cleanedUser);
      return { user: newUser, isNewUser: true };
    }
  },

  async registerWithEmail(email: string, password: string, displayName: string): Promise<{ user: User; isNewUser: boolean }> {
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(credential.user, { displayName });
    const fbUser = credential.user;

    const newUser: User = {
      id: fbUser.uid,
      email: email.toLowerCase(),
      displayName,
      role: UserRole.USER,
      tier: UserTier.FREE,
      createdAt: Date.now(),
      lastLogin: Date.now(),
      subscriptionStatus: 'NONE',
      settings: {
        theme: 'light',
        calendarSynced: false,
        notifications: {
          pushEnabled: true,
          quietHoursEnabled: true,
          quietHoursStart: "22:00",
          quietHoursEnd: "08:00",
          notifyOn: ['nudge_urgent', 'nudge_strategic', 'completed', 'partner_request', 'partner_update', 'daily', 'inactivity_7_days', 'streak_7_day']
        }
      }
    };

    const cleaned = cleanForFirestore(newUser);
    if (cleaned) await setDoc(doc(firestore, 'users', fbUser.uid), cleaned);
    return { user: newUser, isNewUser: true };
  },

  async signInWithEmail(email: string, password: string): Promise<{ user: User; isNewUser: boolean }> {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    const fbUser = credential.user;

    // Check for existing doc under Firebase UID
    const userDoc = await getDoc(doc(firestore, 'users', fbUser.uid));
    if (userDoc.exists()) {
      const userData = userDoc.data() as User;
      await setDoc(doc(firestore, 'users', fbUser.uid), { lastLogin: Date.now() }, { merge: true });
      return { user: { ...userData, lastLogin: Date.now() }, isNewUser: false };
    }

    // Migration: check for old stable ID doc
    const oldStableId = btoa(email.toLowerCase()).replace(/=/g, '').slice(0, 15);
    const oldDoc = await getDoc(doc(firestore, 'users', oldStableId));
    if (oldDoc.exists()) {
      await this.migrateStableIdUser(oldStableId, fbUser.uid, email.toLowerCase());
      const migratedUser = await getDoc(doc(firestore, 'users', fbUser.uid));
      return { user: migratedUser.data() as User, isNewUser: false };
    }

    // No doc found — create fresh (edge case)
    const newUser: User = {
      id: fbUser.uid,
      email: email.toLowerCase(),
      displayName: fbUser.displayName || email.split('@')[0],
      role: UserRole.USER,
      tier: UserTier.FREE,
      createdAt: Date.now(),
      lastLogin: Date.now(),
      subscriptionStatus: 'NONE',
      settings: { theme: 'light', calendarSynced: false }
    };
    const cleaned = cleanForFirestore(newUser);
    if (cleaned) await setDoc(doc(firestore, 'users', fbUser.uid), cleaned);
    return { user: newUser, isNewUser: false };
  },

  async migrateStableIdUser(oldStableId: string, newUid: string, email: string): Promise<void> {
    console.log(`[AUTH] Migrating user data from ${oldStableId} to ${newUid}`);
    const oldUserDoc = await getDoc(doc(firestore, 'users', oldStableId));
    if (!oldUserDoc.exists()) return;

    const oldData = oldUserDoc.data() as User;
    const batch = writeBatch(firestore);

    // Write new user doc with updated ID
    batch.set(doc(firestore, 'users', newUid), { ...oldData, id: newUid, lastLogin: Date.now() });

    // Migrate tasks
    const tasksSnap = await getDocs(query(collection(firestore, 'tasks'), where('userId', '==', oldStableId)));
    tasksSnap.docs.forEach(d => batch.update(d.ref, { userId: newUid }));

    // Migrate categories
    const catsSnap = await getDocs(query(collection(firestore, 'categories'), where('userId', '==', oldStableId)));
    catsSnap.docs.forEach(d => batch.update(d.ref, { userId: newUid }));

    // Migrate notifications
    const notifsSnap = await getDocs(query(collection(firestore, 'user_notifications'), where('userId', '==', oldStableId)));
    notifsSnap.docs.forEach(d => batch.update(d.ref, { userId: newUid }));

    // Migrate broadcasts
    const broadcastsSnap = await getDocs(query(collection(firestore, 'broadcasts'), where('targetUserId', '==', oldStableId)));
    broadcastsSnap.docs.forEach(d => batch.update(d.ref, { targetUserId: newUid }));

    // Delete old user doc
    batch.delete(doc(firestore, 'users', oldStableId));

    await batch.commit();
    console.log(`[AUTH] Migration complete: ${tasksSnap.size} tasks, ${catsSnap.size} categories, ${notifsSnap.size} notifications migrated`);
  },

  async resetPassword(email: string): Promise<void> {
    await sendPasswordResetEmail(auth, email);
  },

  async logout(): Promise<void> {
    await signOut(auth);
  },

  onAuthChange(callback: (user: FirebaseUser | null) => void): () => void {
    return onAuthStateChanged(auth, callback);
  },

  async getUsers(): Promise<User[]> {
    const snapshot = await getDocs(collection(firestore, 'users'));
    return snapshot.docs.map(d => d.data() as User);
  },

  async getUser(userId: string): Promise<User | null> {
    const d = await getDoc(doc(firestore, 'users', userId));
    return d.exists() ? (d.data() as User) : null;
  },

  async updateUser(userId: string, updates: Partial<User>) {
    const userRef = doc(firestore, 'users', userId);
    const cleaned = cleanForFirestore(updates);
    if (cleaned) {
      await setDoc(userRef, cleaned, { merge: true });
    }
  },

  async deleteUser(userId: string) {
    if (!firestore) return;
    const tasksQuery = query(collection(firestore, 'tasks'), where('userId', '==', userId));
    const catsQuery = query(collection(firestore, 'categories'), where('userId', '==', userId));
    const notifsQuery = query(collection(firestore, 'user_notifications'), where('userId', '==', userId));
    const broadcastsQuery = query(collection(firestore, 'broadcasts'), where('targetUserId', '==', userId));
    const [tasksSnap, catsSnap, notifsSnap, broadcastsSnap] = await Promise.all([
      getDocs(tasksQuery),
      getDocs(catsQuery),
      getDocs(notifsQuery),
      getDocs(broadcastsQuery)
    ]);
    const deletions = [
      deleteDoc(doc(firestore, 'users', userId)),
      ...tasksSnap.docs.map(d => deleteDoc(d.ref)),
      ...catsSnap.docs.map(d => deleteDoc(d.ref)),
      ...notifsSnap.docs.map(d => deleteDoc(d.ref)),
      ...broadcastsSnap.docs.map(d => deleteDoc(d.ref))
    ];
    await Promise.all(deletions);
  },

  async getTasks(userId: string): Promise<Task[]> {
    let q;
    if (userId === 'all_admin_override') {
      q = collection(firestore, 'tasks');
    } else {
      q = query(collection(firestore, 'tasks'), where('userId', '==', userId));
    }
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => d.data() as Task);
  },

  subscribeToTasks(userId: string, callback: (tasks: Task[]) => void): Unsubscribe {
    let q;
    if (userId === 'all_admin_override') {
      q = collection(firestore, 'tasks');
    } else {
      q = query(collection(firestore, 'tasks'), where('userId', '==', userId));
    }
    return onSnapshot(q, (snapshot) => {
      callback(snapshot.docs.map(d => d.data() as Task));
    });
  },

  async createTask(task: Task) {
    await setDoc(doc(firestore, 'tasks', task.id), cleanForFirestore(task));
  },

  async updateTask(taskId: string, updates: Partial<Task>) {
    await updateDoc(doc(firestore, 'tasks', taskId), cleanForFirestore(updates));
  },

  async promoteSubtask(parentTask: Task, subtaskId: string, user: User, options?: { inheritPartners?: boolean }): Promise<string> {
    const subtask = parentTask.subtasks.find(s => s.id === subtaskId);
    if (!subtask) throw new Error("Subtask not found");

    if (subtask.promoted && subtask.promotedTaskId) return subtask.promotedTaskId;

    const newTaskId = Math.random().toString(36).substr(2, 9);
    const now = Date.now();

    const newTask: Task = {
      id: newTaskId,
      taskId: newTaskId,
      userId: user.id,
      title: subtask.text,
      description: `Promoted from parent task: "${parentTask.title}"`,
      initialQuadrant: parentTask.currentQuadrant,
      currentQuadrant: parentTask.currentQuadrant,
      quadrantHistory: [],
      status: 'NEW',
      isCompleted: false,
      hasSubtasks: false,
      subtaskCount: 0,
      completedSubtaskCount: 0,
      subtasks: [],
      createdAt: now,
      updatedAt: now,
      completedAt: null,
      category: parentTask.category,
      tags: [],
      source: 'USER_CREATED',
      partners: options?.inheritPartners ? parentTask.partners : [],
      originType: 'promoted_subtask',
      parentTaskId: parentTask.id,
      parentSubtaskId: subtaskId,
      promotion: {
        promotedAt: now,
        promotedBy: user.id
      }
    };

    const updatedSubtasks = parentTask.subtasks.map(s =>
      s.id === subtaskId
        ? { ...s, promoted: true, promotedTaskId: newTaskId, promotedAt: now }
        : s
    );

    const batch = writeBatch(firestore);
    batch.set(doc(firestore, 'tasks', newTaskId), cleanForFirestore(newTask));
    batch.update(doc(firestore, 'tasks', parentTask.id), {
      subtasks: cleanForFirestore(updatedSubtasks),
      updatedAt: now
    });

    await batch.commit();
    return newTaskId;
  },

  async syncParentSubtaskCompletion(parentTaskId: string, subtaskId: string, isCompleted: boolean) {
    const parentRef = doc(firestore, 'tasks', parentTaskId);
    const snap = await getDoc(parentRef);
    if (!snap.exists()) return;

    const parentTask = snap.data() as Task;
    const subtasks = parentTask.subtasks || [];

    const updatedSubtasks = subtasks.map(s => {
      if (s.id === subtaskId && s.completed !== isCompleted) {
        return { ...s, completed: isCompleted };
      }
      return s;
    });

    const hasChange = updatedSubtasks.some((s, i) => s.completed !== (subtasks[i]?.completed));

    if (hasChange) {
      const completedCount = updatedSubtasks.filter(s => s.completed).length;
      await updateDoc(parentRef, {
        subtasks: cleanForFirestore(updatedSubtasks),
        completedSubtaskCount: completedCount,
        updatedAt: Date.now()
      });
      console.log(`[Vanguard-Service] Synced parent task ${parentTaskId} subtask ${subtaskId} to ${isCompleted}`);
    }
  },

  async getCategories(userId: string): Promise<Category[]> {
    const q = query(collection(firestore, 'categories'), or(where('userId', '==', userId), where('userId', '==', 'system')));
    const snapshot = await getDocs(q);
    const cats = snapshot.docs.map(d => d.data() as Category);
    return cats.length > 0 ? cats : [DEFAULT_CATEGORY];
  },

  subscribeToCategories(userId: string, callback: (categories: Category[]) => void): Unsubscribe {
    const q = query(collection(firestore, 'categories'), or(where('userId', '==', userId), where('userId', '==', 'system')));
    return onSnapshot(q, (snapshot) => {
      const cats = snapshot.docs.map(d => d.data() as Category);
      callback(cats.length > 0 ? cats : [DEFAULT_CATEGORY]);
    });
  },

  async createCategory(cat: Category): Promise<Category> {
    await setDoc(doc(firestore, 'categories', cat.id), cleanForFirestore(cat));
    return cat;
  },

  async deleteCategory(id: string) {
    await deleteDoc(doc(firestore, 'categories', id));
  },

  async getTemplates(): Promise<MessageTemplate[]> {
    const snapshot = await getDocs(collection(firestore, 'templates'));
    return snapshot.docs.map(d => ({ ...d.data(), id: d.id } as MessageTemplate));
  },

  async getTemplate(id: string): Promise<MessageTemplate | null> {
    const d = await getDoc(doc(firestore, 'templates', id));
    if (d.exists()) return ({ ...d.data(), id: d.id } as MessageTemplate);

    if (firestore) {
      const q = query(collection(firestore, 'templates'), where('name', '==', id), limit(1));
      const snap = await getDocs(q);
      if (!snap.empty) return ({ ...snap.docs[0].data(), id: snap.docs[0].id } as MessageTemplate);
    }

    const local = await localStore.getAll<MessageTemplate>('templates');
    return local.find(t => t.id === id || t.name === id) || null;
  },

  async updateTemplate(id: string, updates: Partial<MessageTemplate>) {
    const cleanUpdates = cleanForFirestore(updates);
    if (firestore) {
      await setDoc(doc(firestore, 'templates', id), cleanUpdates, { merge: true });
    }
    await localStore.update('templates', id, cleanUpdates);
  },

  async getAssociations(): Promise<TriggerAssociation[]> {
    const snapshot = await getDocs(collection(firestore, 'associations'));
    return snapshot.docs.map(d => d.data() as TriggerAssociation);
  },

  async getAssociationByEvent(event: SystemEvent): Promise<TriggerAssociation | null> {
    if (firestore) {
      const q = query(collection(firestore, 'associations'), where('systemEvent', '==', event), limit(1));
      const snap = await getDocs(q);
      if (!snap.empty) return snap.docs[0].data() as TriggerAssociation;
    }
    const local = await localStore.getAll<TriggerAssociation>('associations');
    return local.find(a => a.systemEvent === event) || null;
  },

  async createAssociation(assoc: TriggerAssociation) {
    await setDoc(doc(firestore, 'associations', assoc.id), cleanForFirestore(assoc));
  },

  async deleteAssociation(id: string) {
    await deleteDoc(doc(firestore, 'associations', id));
  },

  async getEmailLogs(): Promise<EmailLog[]> {
    const local = await localStore.getAll<EmailLog>('email_logs');
    let remote: EmailLog[] = [];
    if (firestore) {
      try {
        const q = query(collection(firestore, 'email_logs'), orderBy('timestamp', 'desc'), limit(100));
        const snapshot = await getDocs(q);
        remote = snapshot.docs.map(d => d.data() as EmailLog);
      } catch (e) {
        console.warn("[Vanguard] Remote log fetch failed.");
      }
    }

    const mergedMap = new Map<string, EmailLog>();
    [...remote, ...local].forEach(log => {
      const existing = mergedMap.get(log.id);
      const logHops = log.metadata?.trace?.length || 0;
      const existingHops = existing?.metadata?.trace?.length || 0;

      if (!existing || logHops >= existingHops) {
        mergedMap.set(log.id, log);
      }
    });

    return Array.from(mergedMap.values()).sort((a, b) => b.timestamp - a.timestamp).slice(0, 100);
  },

  subscribeToEmailLogs(callback: (logs: EmailLog[]) => void): Unsubscribe {
    if (!firestore) {
      localStore.getAll<EmailLog>('email_logs').then(callback);
      return () => { };
    }

    const q = query(collection(firestore, 'email_logs'), orderBy('timestamp', 'desc'), limit(100));
    return onSnapshot(q, async (snapshot) => {
      const local = await localStore.getAll<EmailLog>('email_logs');
      const remote = snapshot.docs.map(d => d.data() as EmailLog);

      const mergedMap = new Map<string, EmailLog>();
      [...remote, ...local].forEach(log => {
        const existing = mergedMap.get(log.id);
        const logHops = log.metadata?.trace?.length || 0;
        const existingHops = existing?.metadata?.trace?.length || 0;
        if (!existing || logHops >= existingHops) mergedMap.set(log.id, log);
      });

      callback(Array.from(mergedMap.values()).sort((a, b) => b.timestamp - a.timestamp).slice(0, 100));
    });
  },

  async logEmail(log: EmailLog) {
    await localStore.add('email_logs', log);

    if (firestore) {
      setDoc(doc(firestore, 'email_logs', log.id), cleanForFirestore(log)).catch(e => {
        console.warn("[Vanguard] Remote log sync failed.", e);
      });
    }
  },

  async getBroadcasts(user: User): Promise<Broadcast[]> {
    const q = query(collection(firestore, 'broadcasts'), orderBy('timestamp', 'desc'), limit(50));
    const snapshot = await getDocs(q);
    const all = snapshot.docs.map(d => d.data() as Broadcast);
    return all.filter(b => {
      if (b.targetGroup === 'ALL') return true;
      if (b.targetGroup === 'PREMIUM' && (user.tier === UserTier.PREMIUM || user.role === UserRole.ADMIN)) return true;
      if (b.targetGroup === 'FREE' && user.tier === UserTier.FREE) return true;
      if (b.targetGroup === 'ADMINS' && user.role === UserRole.ADMIN) return true;
      if (b.targetGroup === 'INDIVIDUAL' && b.targetUserId === user.id) return true;
      return false;
    });
  },

  async createBroadcast(broadcast: Omit<Broadcast, 'id'>) {
    const id = Math.random().toString(36).substr(2, 9);
    await setDoc(doc(firestore, 'broadcasts', id), cleanForFirestore({ ...broadcast, id }));
  },

  subscribeToBroadcasts(user: User, callback: (broadcasts: Broadcast[]) => void): Unsubscribe {
    const q = query(collection(firestore, 'broadcasts'), orderBy('timestamp', 'desc'), limit(50));
    return onSnapshot(q, (snapshot) => {
      const all = snapshot.docs.map(d => d.data() as Broadcast);
      const filtered = all.filter(b => {
        if (b.targetGroup === 'ALL') return true;
        if (b.targetGroup === 'PREMIUM' && (user.tier === UserTier.PREMIUM || user.role === UserRole.ADMIN)) return true;
        if (b.targetGroup === 'FREE' && user.tier === UserTier.FREE) return true;
        if (b.targetGroup === 'ADMINS' && user.role === UserRole.ADMIN) return true;
        if (b.targetGroup === 'INDIVIDUAL' && b.targetUserId === user.id) return true;
        return false;
      });
      callback(filtered);
    });
  },

  // Feedback
  async submitFeedback(feedback: Omit<Feedback, 'id'>): Promise<string> {
    const id = Math.random().toString(36).substr(2, 9);
    await setDoc(doc(firestore, 'feedback', id), cleanForFirestore({ ...feedback, id }));
    return id;
  },

  async getAllFeedback(): Promise<Feedback[]> {
    const q = query(collection(firestore, 'feedback'), orderBy('createdAt', 'desc'), limit(100));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => d.data() as Feedback);
  },

  async updateFeedbackStatus(id: string, status: FeedbackStatus, adminNotes?: string): Promise<void> {
    const updates: Record<string, any> = { status, updatedAt: Date.now() };
    if (adminNotes !== undefined) updates.adminNotes = adminNotes;
    await setDoc(doc(firestore, 'feedback', id), cleanForFirestore(updates), { merge: true });
  },

  async getUserNotifications(userId: string): Promise<UserNotification[]> {
    const q = query(collection(firestore, 'user_notifications'), where('userId', '==', userId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => d.data() as UserNotification);
  },

  subscribeToUserNotifications(userId: string, callback: (notifs: UserNotification[]) => void): Unsubscribe {
    const q = query(collection(firestore, 'user_notifications'), where('userId', '==', userId));
    return onSnapshot(q, (snapshot) => {
      callback(snapshot.docs.map(d => d.data() as UserNotification));
    });
  },

  async markNotificationRead(userId: string, broadcastId: string) {
    const id = `${userId}_${broadcastId}`;
    await setDoc(doc(firestore, 'user_notifications', id), cleanForFirestore({
      id, userId, broadcastId, read: true, receivedAt: Date.now()
    }));
  },

  async getTrustDocuments(): Promise<TrustDocument[]> {
    const snapshot = await getDocs(collection(firestore, 'trust_documents'));
    return snapshot.docs.map(d => d.data() as TrustDocument);
  },

  async getTrustVersions(docId: string): Promise<TrustVersion[]> {
    const snapshot = await getDocs(collection(firestore, `trust_documents/${docId}/versions`));
    return snapshot.docs.map(d => d.data() as TrustVersion);
  },

  async createTrustDocument(docData: Partial<TrustDocument>, content: string, actor: Actor) {
    const id = Math.random().toString(36).substr(2, 9);
    const newDoc: TrustDocument = {
      id,
      slug: docData.slug!,
      title: docData.title!,
      category: docData.category!,
      status: 'DRAFT',
      currentVersionId: null,
      publishedVersionId: null,
      createdAt: Date.now(),
      createdBy: actor,
      updatedAt: Date.now(),
      updatedBy: actor,
      lastPublishedAt: null,
      lastPublishedBy: actor
    };
    await setDoc(doc(firestore, 'trust_documents', id), cleanForFirestore(newDoc));
    await this.createTrustVersion(id, content, actor, 'Initial Provision');
  },

  async createTrustVersion(docId: string, content: string, actor: Actor, summary: string) {
    const versionId = Math.random().toString(36).substr(2, 9);
    const docRef = doc(firestore, 'trust_documents', docId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return;
    const versions = await this.getTrustVersions(docId);
    const nextNumber = versions.length + 1;
    const newVersion: TrustVersion = {
      versionId,
      versionNumber: nextNumber,
      contentFormat: 'MARKDOWN',
      content,
      effectiveDate: null,
      createdAt: Date.now(),
      createdBy: actor,
      changeSummary: summary,
      isPublished: false
    };
    await setDoc(doc(firestore, `trust_documents/${docId}/versions`, versionId), cleanForFirestore(newVersion));
    await updateDoc(docRef, cleanForFirestore({
      currentVersionId: versionId,
      updatedAt: Date.now(),
      updatedBy: actor
    }));
  },

  async publishTrustVersion(docId: string, versionId: string, actor: Actor) {
    const docRef = doc(firestore, 'trust_documents', docId);
    const versionRef = doc(firestore, `trust_documents/${docId}/versions`, versionId);
    const batch = writeBatch(firestore);
    batch.update(docRef, cleanForFirestore({
      status: 'PUBLISHED',
      publishedVersionId: versionId,
      lastPublishedAt: Date.now(),
      lastPublishedBy: actor
    }));
    batch.update(versionRef, cleanForFirestore({
      isPublished: true,
      effectiveDate: Date.now()
    }));
    await batch.commit();
    await addDoc(collection(firestore, 'trust_audit_log'), cleanForFirestore({
      id: Math.random().toString(36).substr(2, 9),
      docId,
      versionId,
      action: 'PUBLISH',
      actor,
      timestamp: Date.now(),
      details: { versionId }
    }));
  },

  async unpublishTrustDocument(docId: string, actor: Actor) {
    await updateDoc(doc(firestore, 'trust_documents', docId), cleanForFirestore({
      status: 'DRAFT',
      publishedVersionId: null
    }));
  },

  async getTrustAuditLogs(): Promise<TrustAuditLog[]> {
    const q = query(collection(firestore, 'trust_audit_log'), orderBy('timestamp', 'desc'), limit(100));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => d.data() as TrustAuditLog);
  },

  async getSocialLinks(onlyActive?: boolean): Promise<SocialLink[]> {
    let q = query(collection(firestore, 'social_links'), orderBy('sortOrder', 'asc'));
    if (onlyActive) {
      q = query(collection(firestore, 'social_links'), where('isActive', '==', true), orderBy('sortOrder', 'asc'));
    }
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => d.data() as SocialLink);
  },

  async createSocialLink(link: Omit<SocialLink, 'id'>) {
    const id = Math.random().toString(36).substr(2, 9);
    await setDoc(doc(firestore, 'social_links', id), cleanForFirestore({ ...link, id }));
  },

  async updateSocialLink(id: string, updates: Partial<SocialLink>) {
    await updateDoc(doc(firestore, 'social_links', id), cleanForFirestore(updates));
  },

  async deleteSocialLink(id: string) {
    await deleteDoc(doc(firestore, 'social_links', id));
  },

  // Landing Videos
  async getLandingVideos(): Promise<{ id: string; label: string; url: string; order: number; storagePath: string }[]> {
    const q = query(collection(firestore, 'landingVideos'), orderBy('order', 'asc'));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as any));
  },

  uploadLandingVideo(
    file: File,
    label: string,
    onProgress: (pct: number) => void
  ): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const storagePath = `landingVideos/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, storagePath);
      const task = uploadBytesResumable(storageRef, file);

      task.on(
        'state_changed',
        snap => onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
        reject,
        async () => {
          try {
            const url = await getDownloadURL(task.snapshot.ref);
            const snap2 = await getDocs(collection(firestore, 'landingVideos'));
            const order = snap2.size;
            await addDoc(collection(firestore, 'landingVideos'), { label, url, storagePath, order, uploadedAt: Date.now() });
            resolve();
          } catch (e) { reject(e); }
        }
      );
    });
  },

  async deleteLandingVideo(id: string, storagePath: string) {
    await deleteDoc(doc(firestore, 'landingVideos', id));
    try { await deleteObject(ref(storage, storagePath)); } catch (_) {}
  },

  async getSystemConfig(): Promise<SystemConfig | null> {
    const d = await getDoc(doc(firestore, 'config', 'stripe'));
    return d.exists() ? d.data() as SystemConfig : null;
  },

  async updateSystemConfig(config: SystemConfig) {
    await setDoc(doc(firestore, 'config', 'stripe'), cleanForFirestore(config), { merge: true });
  },

  async getPostmarkConfig(): Promise<PostmarkConfig | null> {
    if (firestore) {
      const d = await getDoc(doc(firestore, 'config', 'postmark'));
      return d.exists() ? d.data() as PostmarkConfig : null;
    }
    return null;
  },

  async updatePostmarkConfig(config: PostmarkConfig) {
    await setDoc(doc(firestore, 'config', 'postmark'), cleanForFirestore(config));
  },

  async seedData() {
    const batch = writeBatch(firestore);
    CORE_TEMPLATES.forEach(tmpl => {
      batch.set(doc(firestore, 'templates', tmpl.id), cleanForFirestore(tmpl));
    });
    CORE_ASSOCIATIONS.forEach(assoc => {
      batch.set(doc(firestore, 'associations', assoc.id), cleanForFirestore(assoc));
    });
    batch.set(doc(firestore, 'categories', DEFAULT_CATEGORY.id), cleanForFirestore(DEFAULT_CATEGORY));
    await batch.commit();
  }
};
